// Summit Run. Lilla's game.
//
// Alto's Adventure to look at, SkiFree to play.
//
// The two earlier attempts each got exactly half of that and it took both of
// them to see why. A pure side-on Alto plays nothing like SkiFree: there is no
// left and right, so there is nothing to dodge. A pure top-down SkiFree cannot
// look like Alto, because all of Alto's beauty comes from a HORIZON: sky,
// a low sun, ridgelines fading into haze. Looking straight down at a mountain
// throws every one of those away and leaves a white field with dark blobs on
// it, which is what the last version was.
//
// So the camera sits behind the skier and looks down the fall line at an
// angle. That keeps the horizon, the sky and the layered peaks, and it still
// gives a left and a right to steer between. Objects carry a lateral position
// and a depth, and one divide turns that into a screen position and a scale,
// so things grow as they come at you.
//
// The SkiFree rules on top: steer to miss trees and rocks, cutting hard across
// the hill costs you descent, ramps throw you over everything, slalom gates pay
// out, and on the big mountains something starts following you down.
//
// Everything is drawn with shapes, so there are no assets, it works offline,
// and the whole thing is a few kilobytes.
//
// ---- On the loop ----
//
// It is started imperatively by the button that starts a run, and stopped by
// `finish` or by unmounting. It is deliberately not a useEffect. Two earlier
// versions froze partway through a run because the loop lived in an effect,
// React re-ran that effect for reasons having nothing to do with the game, and
// a cleanup cancelled an animation frame nothing ever rescheduled. A 120Hz
// simulation does not belong on the render lifecycle.
import { useCallback, useEffect, useRef, useState } from "react";
import {BRAND} from "../shared/brand.js";
import { api } from "./api";
import { burstConfetti, sounds } from "./effects";
import { CLIMBS_PER_DAY, MOUNTAINS, paletteFor, scoreRun, type Mountain, type Palette } from "../shared/mountains";
import { storyFor, type MountainStory } from "../shared/mountainStories";
import { startRunMusic, stopRunMusic } from "./runMusic";
import type { Band } from "../shared/economy";

interface GameState {
  enabled: boolean;
  unlocked: boolean;
  climbsLeft: number;
  climbsPerDay: number;
  mountainIndex: number;
  clearedCount: number;
  bonusRun?: { earned: boolean; reason: "lesson" | "quiz" | null };
}

interface Boards {
  me: string;
  personal: Array<{ mountain_index: number; best: number; runs: number }>;
  daily: Array<{ name: string; best: number; mountain_index: number }>;
  allTime: Array<{ name: string; best: number; mountain_index: number }>;
}

type Phase = "ready" | "playing" | "crashed" | "summit";

const W = 360;
const H = 620;
const HORIZON = H * 0.36;
// The skier sits low, so most of the screen is slope you have not reached yet.
const SKI_Y = H * 0.79;

// The projection. An object carries a lateral position `u` (course units, the
// skiable width being roughly -1.6 to 1.6) and a depth `z` in the same units.
// One divide gives screen position and scale.
const ZFAR = 7.5;
const DEPTH = 0.92;
const SPREAD = 196;

function projK(z: number): number {
  return 1 / (1 + Math.max(-0.55, z) * DEPTH);
}

// The mountain is not a plane.
//
// The last version drew the slope as one flat gradient, and no amount of sky
// behind it made the ground read as tilted or as moving. This is the fix: a
// height field along the fall line, sampled in absolute distance so the terrain
// under a tree is the same terrain that was under it a second ago. Crests rise
// into view, dips fall away, and the camera pitches as you go over them.
//
// It deliberately does NOT include the average downhill slope. That part is
// already what the perspective projection means; adding it here would just
// flatten the projection back out. Only the rolls live here.
function roll(d: number): number {
  return Math.sin(d * 0.21) * 1.7
    + Math.sin(d * 0.53) * 0.85
    + Math.sin(d * 1.27) * 0.3;
}
// How many screen pixels a unit of roll is worth at the skier's own depth.
const ROLL_SCALE = 62;

// Per second, never per frame.
const STEP = 1 / 120;
const MAX_FRAME = 0.25;
const MAX_TUMBLES = 3;
const TUMBLE_SECONDS = 0.8;
const RUN_SECONDS = 45;
const AIR_SECONDS = 1.05;
// Cutting across the hill costs descent. This is the one SkiFree rule worth
// copying exactly: it turns steering into a decision instead of a reflex.
const CUT_COST = 0.45;
const LEAN_RATE = 3.6;
const SIDE_RATE = 1.15;
const MAX_U = 1.55;

type Kind = "tree" | "rock" | "ramp" | "gate" | "coin" | "cabin" | "flagline";
const SOLID: ReadonlySet<Kind> = new Set<Kind>(["tree", "rock", "cabin"]);

interface Obj { u: number; z: number; kind: Kind; seed: number; done?: boolean }
interface Puff { x: number; y: number; vx: number; vy: number; life: number }
interface Trail { u: number; z: number }

interface Run {
  dist: number; total: number;
  u: number; lean: number;
  speed: number; topSpeed: number;
  air: number; tumble: number; tumbles: number;
  objs: Obj[]; nextObj: number;
  puffs: Puff[]; trail: Trail[];
  coins: number; gates: number; jumps: number;
  chase: number | null;     // 0 = right behind you, 1 = far back
  caught: boolean;
}

// `sandbox` is the parent's copy: every mountain unlocked, no daily cap, and
// nothing written to the child's record. A parent should be able to see what
// they are switching on before they switch it on.
export function SummitRun({ band, onClose, onPlayed, sandbox = false }: {
  band: Band; onClose: () => void; onPlayed?: () => void; sandbox?: boolean;
}) {
  const [state, setState] = useState<GameState | null>(
    sandbox ? { enabled: true, unlocked: true, climbsLeft: 99, climbsPerDay: 99, mountainIndex: 0, clearedCount: 0 } : null,
  );
  const [phase, setPhase] = useState<Phase>("ready");
  const [reached, setReached] = useState(0);
  const [score, setScore] = useState({ coins: 0, gates: 0, jumps: 0, tumbles: 0 });
  const [sheet, setSheet] = useState<"none" | "map" | "boards" | "story">("none");
  const [boards, setBoards] = useState<Boards | null>(null);
  // Which mountain's entry to show. Defaults to the one you are about to ski,
  // but the list can point it anywhere.
  const [storyIndex, setStoryIndex] = useState<number | null>(null);
  const [result, setResult] = useState<{ score: number; best: number; beat: boolean } | null>(null);
  const [message, setMessage] = useState("");

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const runRef = useRef<Run | null>(null);
  const inputRef = useRef({ left: false, right: false, pointer: null as number | null });
  const loopRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (sandbox) return;
    void api<GameState>("/api/student/game").then(setState).catch(() => setState(null));
  }, [sandbox]);

  // Closing the overlay mid-run must not leave a loop spinning or a pad droning
  // behind it. This is the only lifecycle hook the game loop touches.
  useEffect(() => () => { loopRef.current?.(); loopRef.current = null; stopRunMusic(); }, []);

  const mountain: Mountain = MOUNTAINS[state?.mountainIndex ?? 0] ?? MOUNTAINS[0]!;
  const index = state?.mountainIndex ?? 0;

  const finish = useCallback(async (summited: boolean, pct: number, run: Run) => {
    loopRef.current?.();
    loopRef.current = null;
    stopRunMusic();
    setPhase(summited ? "summit" : "crashed");
    setReached(pct);
    setScore({ coins: run.coins, gates: run.gates, jumps: run.jumps, tumbles: run.tumbles });
    // Scored locally too, so the number appears the instant the run ends. The
    // server recomputes it from the same function and its answer is the one
    // that gets stored.
    const local = scoreRun({
      mountainIndex: index, reached: pct, summited,
      coins: run.coins, flags: run.gates, flips: run.jumps,
    });
    setResult({ score: local, best: local, beat: false });
    if (summited) { burstConfetti(); sounds.milestone(band); } else { sounds.tap(band); }
    // A parent's practice run is not a descent and does not belong in a kid's
    // history or against their daily count.
    if (sandbox) {
      if (summited) setState((prev) => prev && { ...prev, clearedCount: prev.clearedCount + 1 });
      return;
    }
    try {
      const next = await api<GameState & { summited: boolean; score: number; personalBest: number; beatBest: boolean }>(
        "/api/student/game/run",
        {
          method: "POST",
          body: JSON.stringify({
            mountainIndex: index, reached: pct, summited,
            collected: run.coins + run.gates,
            coins: run.coins, flags: run.gates, flips: run.jumps,
          }),
        },
      );
      setResult({ score: next.score, best: next.personalBest, beat: next.beatBest });
      if (next.beatBest) burstConfetti();
      setState(next);
      setBoards(null);
      onPlayed?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save that run");
    }
  }, [band, index, onPlayed, sandbox]);

  // Everything the loop needs that can change between renders lives here, so
  // the loop never has to be rebuilt when React re-renders. Written in an
  // effect rather than during render, which React forbids.
  const liveRef = useRef({ finish, index, mountain, band });
  useEffect(() => { liveRef.current = { finish, index, mountain, band }; });

  const beginLoop = useCallback(() => {
    loopRef.current?.();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    // Match the backing store to the device pixel ratio, or every silhouette is
    // soft on a retina screen no matter how good the art is.
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const { index: mIndex, mountain: mtn, band: mBand } = liveRef.current;
    const pal = paletteFor(mIndex);
    // Roughly one event a second on Fuji, and most events are a small cluster
    // rather than a single object, so there is a line to pick rather than a
    // thing to sidestep. The previous pass was so sparse it was a slideshow.
    const spacing = 4.4 - mIndex * 0.26;
    // Something starts following you on the back half of the harder mountains,
    // so the youngest kids on Fuji never meet it.
    const chaseFrom = mIndex >= 4 ? 0.6 : null;

    let frameId = 0;
    let stopped = false;
    let previous = performance.now();
    let accumulator = 0;
    let hudAt = 0;

    loopRef.current = () => { stopped = true; cancelAnimationFrame(frameId); };

    const tick = (dt: number): boolean => {
      const run = runRef.current;
      if (!run) return false;

      // Steering. Held keys or a dragged finger both feed the same lean.
      const input = inputRef.current;
      let want = 0;
      if (input.left) want -= 1;
      if (input.right) want += 1;
      if (input.pointer !== null) want = Math.max(-1, Math.min(1, (input.pointer - W / 2) / (W * 0.3)));
      if (run.tumble > 0) want = 0;
      run.lean += (want - run.lean) * (1 - Math.exp(-LEAN_RATE * dt));

      if (run.tumble > 0) {
        run.tumble = Math.max(0, run.tumble - dt);
        run.speed += (run.topSpeed * 0.3 - run.speed) * (1 - Math.exp(-5 * dt));
      } else {
        run.speed += (run.topSpeed - run.speed) * (1 - Math.exp(-0.55 * dt));
      }
      if (run.air > 0) run.air = Math.max(0, run.air - dt);

      const cut = Math.abs(run.lean);
      const descent = run.speed * (1 - cut * CUT_COST);
      run.dist += descent * dt;
      run.u = Math.max(-MAX_U, Math.min(MAX_U, run.u + run.lean * SIDE_RATE * dt));

      // Carved snow off the edges, and a trail behind.
      if (run.tumble === 0 && run.air === 0 && cut > 0.3 && Math.random() < dt * 40) {
        run.puffs.push({
          x: W / 2 - run.lean * 12, y: SKI_Y + 8,
          vx: -run.lean * 70 + (Math.random() - 0.5) * 40, vy: 30 + Math.random() * 50,
          life: 0.3 + Math.random() * 0.35,
        });
      }
      if (run.air === 0) {
        run.trail.push({ u: run.u, z: 0 });
        if (run.trail.length > 40) run.trail.shift();
      }
      for (const t of run.trail) t.z -= descent * dt;
      run.trail = run.trail.filter((t) => t.z > -0.5);

      // Spawning. One thing at a time, well spaced, placed within reach.
      while (run.nextObj < run.dist + ZFAR) {
        const pick = Math.random();
        const z = run.nextObj - run.dist;
        const centre = run.u + (Math.random() - 0.5) * 2.2;
        if (pick < 0.15) {
          // A ramp with its coin arc. Always alone, so the reward is reachable.
          run.objs.push({ u: centre, z, kind: "ramp", seed: Math.random() });
          for (let i = 0; i < 4; i += 1) {
            run.objs.push({ u: centre, z: z - 0.5 - i * 0.34, kind: "coin", seed: i });
          }
        } else if (pick < 0.33) {
          run.objs.push({ u: centre, z, kind: "gate", seed: Math.random() });
        } else if (pick < 0.4) {
          run.objs.push({ u: centre, z, kind: "cabin", seed: Math.random() });
        } else {
          // A cluster with a gap in it. Two or three obstacles spread across the
          // run, deliberately leaving one line through: that is a decision, and
          // a single obstacle in an empty field is not.
          const n = pick < 0.68 ? 2 : 3;
          const gapAt = Math.floor(Math.random() * (n + 1));
          for (let i = 0; i <= n; i += 1) {
            if (i === gapAt) continue;
            run.objs.push({
              u: centre + (i - n / 2) * 0.62,
              z: z + (Math.random() - 0.5) * 0.5,
              kind: Math.random() < 0.42 ? "rock" : "tree",
              seed: Math.random(),
            });
          }
        }
        run.nextObj += spacing * (0.72 + Math.random() * 0.55);
      }
      // A treeline down both edges, well outside the skiable width, purely so
      // the run has sides. Cheap, and it does more for depth than anything.
      while (run.objs.filter((o) => o.kind === "flagline").length < 26) {
        const far = run.objs.reduce((m, o) => (o.kind === "flagline" ? Math.max(m, o.z) : m), 0);
        const z = Math.max(far + 0.55, 0.55);
        if (z > ZFAR) break;
        for (const side of [-1, 1]) {
          run.objs.push({ u: side * (2.1 + Math.random() * 0.9), z, kind: "flagline", seed: Math.random() });
        }
      }

      for (const o of run.objs) o.z -= descent * dt;
      run.objs = run.objs.filter((o) => o.z > -0.7);

      // Everything resolves at the single frame an object reaches the skier.
      // Cheap, exact, and impossible to tunnel through.
      for (const o of run.objs) {
        if (o.done || o.z > 0 || o.z < -0.35 || o.kind === "flagline") continue;
        o.done = true;
        const du = Math.abs(o.u - run.u);
        if (o.kind === "coin") {
          if (du < 0.3) { run.coins += 1; sounds.fuelGain(mBand, 1); }
        } else if (o.kind === "gate") {
          if (du < 0.42) { run.gates += 1; sounds.fuelGain(mBand, 1); }
        } else if (o.kind === "ramp") {
          // A ramp is pure gift: it throws you up, it can never hurt you.
          if (du < 0.38 && run.tumble === 0) {
            run.air = AIR_SECONDS; run.jumps += 1; burst(run, 14); sounds.tap(mBand);
          }
        } else if (SOLID.has(o.kind) && run.air === 0 && run.tumble === 0) {
          // In the air you clear everything. That is the whole point of a ramp.
          if (du < (o.kind === "cabin" ? 0.34 : 0.2)) {
            run.tumble = TUMBLE_SECONDS; run.tumbles += 1; run.speed *= 0.4;
            burst(run, 20); sounds.tap(mBand);
            if (run.tumbles >= MAX_TUMBLES) {
              void liveRef.current.finish(false, Math.min(99, Math.round((run.dist / run.total) * 100)), run);
              return false;
            }
          }
        }
      }

      // The chaser. Not a punishment: it only appears near the end, it is
      // slower than a clean line, and being caught still keeps your score.
      const progress = run.dist / run.total;
      if (chaseFrom !== null && run.chase === null && progress > chaseFrom) run.chase = 1;
      if (run.chase !== null && !run.caught) {
        // Closes when you are slow, drops back when you are running straight.
        run.chase += (descent / run.topSpeed - 0.82) * 0.5 * dt;
        run.chase = Math.min(1.2, run.chase);
        if (run.chase <= 0) {
          run.caught = true;
          void liveRef.current.finish(false, Math.min(99, Math.round(progress * 100)), run);
          return false;
        }
      }

      for (const p of run.puffs) { p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt; }
      run.puffs = run.puffs.filter((p) => p.life > 0);

      // A last line of defence. If any number ever went non-finite the run
      // would sit there looking frozen, which is the failure this whole
      // structure exists to prevent. End it cleanly instead of hanging.
      if (!Number.isFinite(run.dist) || !Number.isFinite(run.u) || !Number.isFinite(run.speed)) {
        void liveRef.current.finish(false, 0, run);
        return false;
      }
      if (run.dist >= run.total) {
        void liveRef.current.finish(true, 100, run);
        return false;
      }
      return true;
    };

    const frame = (now: number) => {
      const run = runRef.current;
      if (!run || stopped) return;
      accumulator += Math.min(MAX_FRAME, (now - previous) / 1000);
      previous = now;
      while (accumulator >= STEP) {
        if (!tick(STEP)) { stopped = true; return; }
        accumulator -= STEP;
      }
      draw(ctx, run, mtn, pal);
      if (now - hudAt > 140) {
        hudAt = now;
        setReached(Math.min(99, Math.round((run.dist / run.total) * 100)));
        setScore({ coins: run.coins, gates: run.gates, jumps: run.jumps, tumbles: run.tumbles });
      }
      frameId = requestAnimationFrame(frame);
    };
    frameId = requestAnimationFrame(frame);
  }, []);

  const start = useCallback(() => {
    if (!state || state.climbsLeft <= 0) return;
    // Gentle on Fuji, quick on Everest, and every run opens below its own top
    // speed so the first thing a kid meets is an empty slope.
    const topSpeed = 4.8 + index * 0.32;
    runRef.current = {
      dist: 0, total: topSpeed * RUN_SECONDS,
      u: 0, lean: 0,
      speed: topSpeed * 0.55, topSpeed,
      air: 0, tumble: 0, tumbles: 0,
      objs: [], nextObj: 9,
      puffs: [], trail: [],
      coins: 0, gates: 0, jumps: 0,
      chase: null, caught: false,
    };
    setScore({ coins: 0, gates: 0, jumps: 0, tumbles: 0 });
    setResult(null);
    startRunMusic(index);
    setReached(0); setMessage(""); setSheet("none"); setPhase("playing");
    beginLoop();
  }, [state, index, beginLoop]);

  // Keyboard for laptops, drag and buttons for phones.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); inputRef.current.left = true; }
      if (e.key === "ArrowRight") { e.preventDefault(); inputRef.current.right = true; }
      if (e.key === " " && phase !== "playing") { e.preventDefault(); start(); }
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") inputRef.current.left = false;
      if (e.key === "ArrowRight") inputRef.current.right = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [onClose, phase, start]);

  const loadBoards = () => {
    if (boards || sandbox) return;
    void api<Boards>("/api/student/game/boards").then(setBoards).catch(() => setBoards(null));
  };

  const track = (clientX: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    inputRef.current.pointer = ((clientX - rect.left) / rect.width) * W;
  };

  if (!state) return null;

  const cleared = state.mountainIndex;
  const allDone = state.clearedCount >= MOUNTAINS.length;

  return (
    <div className="run-overlay" role="dialog" aria-label={BRAND.gameName}>
      <div className="run-shell">
        <header className="run-head">
          <div>
            <strong>{mountain.name}</strong>
            <small>{mountain.where} · {mountain.feet.toLocaleString()} ft{sandbox ? " · practice" : ""}</small>
          </div>
          <button className="small-button" onClick={onClose}>Close</button>
        </header>

        <div className="run-stage">
          <canvas
            ref={canvasRef} width={W} height={H} className="run-canvas"
            // Steering is the whole control scheme now that the arrow buttons
            // are gone: hold anywhere on the hill and slide. Pointer capture
            // means a finger that wanders off the canvas keeps steering rather
            // than silently letting go at the worst moment.
            onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); track(e.clientX); }}
            onPointerMove={(e) => { if (e.buttons) track(e.clientX); }}
            onPointerUp={() => { inputRef.current.pointer = null; }}
            onPointerCancel={() => { inputRef.current.pointer = null; }}
          />

          {phase === "playing" && (
            <>
              <div className="run-hud">
                <span>{reached}%</span>
                <span className="run-hud-mid">
                  {score.coins > 0 && <em>🪙 {score.coins}</em>}
                  {score.gates > 0 && <em>🚩 {score.gates}</em>}
                  {score.jumps > 0 && <em>🎿 {score.jumps}</em>}
                </span>
                <span aria-label="Falls left">
                  {"●".repeat(MAX_TUMBLES - score.tumbles)}<i>{"○".repeat(score.tumbles)}</i>
                </span>
              </div>
            </>
          )}

          {phase !== "playing" && (
            <div className="run-panel">
              {sheet === "map" ? (
                <>
                  <h3>The ten runs</h3>
                  <MountainList
                    cleared={cleared} sandbox={sandbox} current={state.mountainIndex}
                    onPick={(i) => setState((prev) => prev && { ...prev, mountainIndex: i })}
                    onRead={(i) => { setStoryIndex(i); setSheet("story"); }}
                  />
                  <button className="button wide" onClick={() => setSheet("none")}>Back</button>
                </>
              ) : sheet === "story" ? (
                <>
                  <StorySheet index={storyIndex ?? index} />
                  <button className="button wide" onClick={() => setSheet("none")}>Back</button>
                </>
              ) : sheet === "boards" ? (
                <>
                  <h3>Best runs</h3>
                  <BoardSheet boards={boards} sandbox={sandbox} />
                  <button className="button wide" onClick={() => setSheet("none")}>Back</button>
                </>
              ) : (
                <>
                  {phase === "ready" && (
                    <>
                      <h3>{sandbox ? "Try any run" : `${state.clearedCount} of ${MOUNTAINS.length} skied`}</h3>
                      <p>{mountain.fact}</p>
                      <button className="run-readlink strong" onClick={() => { setStoryIndex(index); setSheet("story"); }}>
                        📖 Read about {mountain.name}
                      </button>
                      <p className="run-hint">
                        Hold a finger on the hill and slide it left or right. Arrow keys work too.<br />
                        Miss the trees. Hit a blue ramp and you fly over everything.
                        Turning hard slows you down.
                      </p>
                    </>
                  )}
                  {phase === "crashed" && (
                    <>
                      <h3>You made it {reached}% down</h3>
                      <p>{summary(score)}</p>
                      <ScoreLine result={result} />
                    </>
                  )}
                  {phase === "summit" && (
                    <>
                      <h3>🎿 {mountain.name}, all the way down</h3>
                      <p>{summary(score)}</p>
                      <ScoreLine result={result} />
                      <p>{sandbox
                        ? "Nice. Pick another and go again."
                        : allDone
                          ? "That's all ten. Everest and everything under it."
                          : `${MOUNTAINS[state.mountainIndex]?.name} is unlocked.`}</p>
                    </>
                  )}
                  {message && <p className="form-error">{message}</p>}
                  {(phase === "crashed" || phase === "summit") && (
                    <button className="run-readlink strong" onClick={() => { setStoryIndex(index); setSheet("story"); }}>
                      📖 While you catch your breath · read about {mountain.name}
                    </button>
                  )}

                  {!sandbox && state.bonusRun?.earned && state.climbsLeft > 0 && <p className="run-bonus">⭐ Bonus run today for a perfect {state.bonusRun.reason ?? "lesson"}!</p>}
                  {state.climbsLeft > 0 ? (
                    <button className="button primary wide" onClick={start}>
                      {phase === "ready" ? "Drop in" : "Go again"}{sandbox ? "" : ` · ${state.climbsLeft} left today`}
                    </button>
                  ) : (
                    <p className="run-done">
                      You skied all {state.climbsPerDay} today{state.bonusRun?.earned ? ", bonus run included" : ""}. Back tomorrow for {CLIMBS_PER_DAY} more.<br />
                      <b>Have a read while you wait.</b>
                    </p>
                  )}
                  <div className="run-links">
                    <button className="small-button" onClick={() => setSheet("map")}>All ten runs</button>
                    <button className="small-button" onClick={() => { setSheet("boards"); loadBoards(); }}>Best runs</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function burst(run: Run, n: number) {
  for (let i = 0; i < n; i += 1) {
    run.puffs.push({
      x: W / 2, y: SKI_Y + 6,
      vx: (Math.random() - 0.5) * 170, vy: 20 + Math.random() * 110,
      life: 0.3 + Math.random() * 0.4,
    });
  }
}

function summary(score: { coins: number; gates: number; jumps: number }) {
  const bits = [
    score.jumps > 0 && `${score.jumps} jump${score.jumps === 1 ? "" : "s"}`,
    score.gates > 0 && `${score.gates} gate${score.gates === 1 ? "" : "s"}`,
    score.coins > 0 && `${score.coins} coin${score.coins === 1 ? "" : "s"}`,
  ].filter(Boolean) as string[];
  if (!bits.length) return "Aim for a blue ramp next time. You clear everything while you are in the air.";
  return `${bits.join(" · ")}.`;
}

// The encyclopedia entry for a mountain. Deliberately reachable in two places
// and forced in neither: before a run for the kid who wants to know where they
// are, and after one, which is the moment they are sitting still anyway.
function StorySheet({ index }: { index: number }) {
  const mountain = MOUNTAINS[Math.max(0, Math.min(MOUNTAINS.length - 1, index))]!;
  const story: MountainStory | null = storyFor(mountain.name);
  if (!story) return <p className="run-hint">No entry for this one yet.</p>;
  return (
    <div className="run-story">
      <h3>{story.name}</h3>
      <p className="run-story-where">
        {mountain.where} · {mountain.feet.toLocaleString()} ft
      </p>
      <p className="run-story-hook">{story.hook}</p>
      <p className="run-story-meaning"><b>The name:</b> {story.meaning}</p>
      {story.story.map((para) => <p key={para.slice(0, 24)}>{para}</p>)}
      <p className="run-story-meaning"><b>First to the top:</b> {story.firstUp}</p>
      <h4>Things worth knowing</h4>
      <ul className="run-story-facts">
        {story.didYouKnow.map((fact) => <li key={fact.slice(0, 24)}>{fact}</li>)}
      </ul>
    </div>
  );
}

function ScoreLine({ result }: { result: { score: number; best: number; beat: boolean } | null }) {
  if (!result) return null;
  return (
    <div className={`run-score${result.beat ? " pb" : ""}`}>
      <strong>{result.score.toLocaleString()}</strong>
      <span>{result.beat ? "New personal best on this mountain" : `Your best here: ${result.best.toLocaleString()}`}</span>
    </div>
  );
}

// The locked list. Sequential unlock was already enforced on the server, but a
// lock a kid cannot see is just a thing that mysteriously does not work, and a
// lock they can see is a reason to come back tomorrow.
function MountainList({ cleared, current, sandbox, onPick, onRead }: {
  cleared: number; current: number; sandbox: boolean;
  onPick: (i: number) => void; onRead: (i: number) => void;
}) {
  return (
    <ol className="run-map">
      {MOUNTAINS.map((m, i) => {
        const done = i < cleared;
        const locked = !sandbox && i > cleared;
        return (
          <li key={m.name} className={`run-map-row${locked ? " locked" : ""}${i === current ? " current" : ""}`}>
            <button disabled={locked || (!sandbox && i !== current)} onClick={() => onPick(i)}>
              <span className="run-map-mark">
                {locked ? "🔒" : <PeakGlyph profile={m.profile} dim={!done && i !== current} />}
              </span>
              <span className="run-map-name">{m.name}</span>
              <span className="run-map-feet">{locked ? "Locked" : `${m.feet.toLocaleString()} ft`}</span>
            </button>
            {/* Reading about a mountain is never locked. A kid can learn about
                Everest long before the game will let them ski it. */}
            <button className="run-map-read" onClick={() => onRead(i)} aria-label={`Read about ${m.name}`}>📖</button>
          </li>
        );
      })}
    </ol>
  );
}

function PeakGlyph({ profile, dim }: { profile: [number, number][]; dim?: boolean }) {
  const points = [[0, 1] as [number, number], ...profile.map(([x, y]) => [x, 1 - y] as [number, number]), [1, 1] as [number, number]]
    .map(([x, y]) => `${(x * 46).toFixed(1)},${(y * 28).toFixed(1)}`).join(" ");
  return (
    <svg className="peak-glyph" viewBox="0 0 46 28" width="46" height="28" aria-hidden="true">
      <polygon points={points} fill={dim ? "rgba(148,163,184,.45)" : "rgba(226,232,240,.92)"} />
    </svg>
  );
}

// Two boards, both scoped to the family.
//
// There is no public or nationwide board, and that is a decision rather than a
// gap. Publishing children's names next to scores where strangers can read them
// is the one thing this app has been careful not to do anywhere else. If other
// families ever use Summit, the safe version is an anonymous percentile:
// "faster than 78% of runs today", no names, nothing stored about anyone
// else's child.
function BoardSheet({ boards, sandbox }: { boards: Boards | null; sandbox: boolean }) {
  if (sandbox) return <p className="run-hint">Practice runs are not scored and never appear on a board.</p>;
  if (!boards) return <p className="run-hint">Loading…</p>;
  const rows = (list: Boards["daily"]) => list.length === 0
    ? <li className="run-board-empty">Nobody has skied yet.</li>
    : list.map((r, i) => (
      <li key={r.name} className={r.name === boards.me ? "me" : ""}>
        <span className="run-board-rank">{i + 1}</span>
        <span className="run-board-name">{r.name}</span>
        <span className="run-board-where">{MOUNTAINS[r.mountain_index]?.name}</span>
        <b>{r.best.toLocaleString()}</b>
      </li>
    ));

  return (
    <div className="run-boards">
      <h4>Your best on each run</h4>
      <ul className="run-board">
        {boards.personal.length === 0
          ? <li className="run-board-empty">No runs yet. The first one sets the bar.</li>
          : boards.personal.map((p) => (
            <li key={p.mountain_index}>
              <span className="run-board-rank">{p.mountain_index + 1}</span>
              <span className="run-board-name">{MOUNTAINS[p.mountain_index]?.name}</span>
              <span className="run-board-where">{p.runs} run{p.runs === 1 ? "" : "s"}</span>
              <b>{p.best.toLocaleString()}</b>
            </li>
          ))}
      </ul>
      <h4>Today, everyone</h4>
      <ul className="run-board">{rows(boards.daily)}</ul>
      <h4>All time</h4>
      <ul className="run-board">{rows(boards.allTime)}</ul>
    </div>
  );
}

// ---- Drawing ----

// Blends two hex colours. Used for aerial perspective: distant things are not
// smaller versions of near things, they are paler ones, and doing that is most
// of the difference between "flat vector art" and "a mountain".
function mix(a: string, b: string, t: number): string {
  const hex = (s: string) => [1, 3, 5].map((i) => parseInt(s.slice(i, i + 2), 16));
  const [ar, ag, ab] = hex(a);
  const [br, bg, bb] = hex(b);
  const k = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(ar! + (br! - ar!) * k)},${Math.round(ag! + (bg! - ag!) * k)},${Math.round(ab! + (bb! - ab!) * k)})`;
}

function draw(ctx: CanvasRenderingContext2D, run: Run, mountain: Mountain, pal: Palette) {
  // Camera pitch. Crest a roller and the horizon drops away; drop into a dip
  // and it rises. Kept small on purpose: this is the difference between a slope
  // and a picture of a slope, and also the quickest way to make someone queasy.
  const here = roll(run.dist);
  const pitch = Math.max(-26, Math.min(26, (roll(run.dist + 3.2) - here) * 17));
  const horizon = HORIZON + pitch;

  // Screen y for a point at depth z, sitting on the terrain.
  const groundY = (z: number, k: number) =>
    horizon + (SKI_Y - horizon) * k - (roll(run.dist + z) - here) * k * ROLL_SCALE;

  const sunX = W * 0.74;
  const sunY = H * 0.05 + pal.sunY * (horizon - H * 0.05);

  // Sky. Three stops, because the band just above the horizon is where all the
  // colour in a real sky lives and a two-stop gradient averages it into mud.
  const sky = ctx.createLinearGradient(0, 0, 0, horizon);
  sky.addColorStop(0, pal.sky[0]);
  sky.addColorStop(0.58, pal.sky[1]);
  sky.addColorStop(1, pal.sky[2]);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, horizon + 1);

  if (pal.stars > 0) {
    const rand = (n: number) => { const v = Math.sin(n * 12.9898) * 43758.5453; return v - Math.floor(v); };
    for (let i = 0; i < 70; i += 1) {
      const tw = 0.5 + 0.5 * Math.sin(run.dist * 1.5 + i);
      ctx.fillStyle = `rgba(255,255,255,${pal.stars * (0.2 + tw * 0.55)})`;
      ctx.fillRect(rand(i) * W, rand(i + 99) * horizon * 0.92, rand(i + 7) > 0.87 ? 2.2 : 1.3, 1.4);
    }
  }

  const halo = ctx.createRadialGradient(sunX, sunY, 4, sunX, sunY, 110);
  halo.addColorStop(0, pal.sun);
  halo.addColorStop(0.16, `${pal.sun}55`);
  halo.addColorStop(1, `${pal.sun}00`);
  ctx.fillStyle = halo;
  ctx.beginPath(); ctx.arc(sunX, sunY, 110, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = pal.sun;
  ctx.beginPath(); ctx.arc(sunX, sunY, pal.stars > 0.7 ? 13 : 19, 0, Math.PI * 2); ctx.fill();

  // Three ranges behind the run, each paler and slower than the one in front.
  ctx.fillStyle = mix(pal.far, pal.sky[2], 0.45);
  drawRange(ctx, run.u * 8, horizon + 3, 54, 118, 0.7);
  ctx.fillStyle = mix(pal.far, pal.sky[2], 0.15);
  drawRange(ctx, run.u * 16 + 40, horizon + 3, 40, 74, 1.15);

  // The mountain you are on, standing furthest back and largest.
  ctx.fillStyle = mix(pal.peak, pal.sky[2], 0.3);
  const px = -run.u * 26 - W * 0.35;
  tracePeak(ctx, mountain.profile, px, px + W * 1.7, horizon + 3, 150);
  ctx.fill();

  ctx.fillStyle = pal.near;
  drawRange(ctx, run.u * 30 + 90, horizon + 4, 26, 46, 1.7);

  // The snow, from a lit band at the horizon down to shade at your feet.
  const snow = ctx.createLinearGradient(0, horizon, 0, H);
  snow.addColorStop(0, mix(pal.snow[0], pal.sky[2], 0.35));
  snow.addColorStop(0.22, pal.snow[0]);
  snow.addColorStop(1, pal.snow[1]);
  ctx.fillStyle = snow;
  ctx.fillRect(0, horizon, W, H - horizon);

  // The sun's light spilling onto the slope.
  const glow = ctx.createRadialGradient(sunX, horizon, 10, sunX, horizon, 320);
  glow.addColorStop(0, `${pal.sun}33`);
  glow.addColorStop(1, `${pal.sun}00`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, horizon, W, H - horizon);

  // Contour bands: the shading between fixed lines of latitude on the mountain,
  // drawn in absolute distance so they slide up and over the rollers with the
  // terrain. This is what actually says "the ground is tilted and I am moving
  // down it". A flat gradient says nothing, however pretty it is.
  // The slope is convex across the fall line, so a contour is an arc that rides
  // up at the edges, not a horizontal line. Drawn as flat rectangles these read
  // as a striped wall; drawn as arcs they read as a hillside. The curvature is
  // scaled by depth, so far contours are nearly flat and near ones bow hard.
  const BAND = 0.42;
  const contourY = (z: number, k: number, xn: number) =>
    groundY(z, k) - xn * xn * 78 * k;

  let edge = Math.ceil(run.dist / BAND) * BAND;
  for (let i = 0; i < 34; i += 1, edge += BAND) {
    const z0 = edge - run.dist;
    const z1 = z0 + BAND;
    if (z1 > ZFAR + 2.5) break;
    const k0 = projK(z0);
    const k1 = projK(z1);
    if (groundY(z0, k0) < horizon - 2) continue;
    ctx.beginPath();
    for (let x = -20; x <= W + 20; x += 20) ctx.lineTo(x, contourY(z0, k0, (x - W / 2) / (W / 2)));
    for (let x = W + 20; x >= -20; x -= 20) ctx.lineTo(x, contourY(z1, k1, (x - W / 2) / (W / 2)));
    ctx.closePath();
    // Alternating bands, strongest underfoot and fading into the haze.
    ctx.fillStyle = i % 2 === 0
      ? `rgba(116,142,182,${0.07 * k0})`
      : `rgba(255,255,255,${0.13 * k0})`;
    ctx.fill();
  }

  const sxOf = (u: number, k: number) => W / 2 + (u - run.u) * SPREAD * k;
  const syOf = (k: number) => horizon + (SKI_Y - horizon) * k;
  // Anything standing on the snow follows the same cross-slope curve the
  // contours do, so a tree out at the edge sits on the hill rather than above it.
  const standY = (z: number, k: number, x: number) =>
    groundY(z, k) - ((x - W / 2) / (W / 2)) ** 2 * 78 * k;

  // Carved trail behind the skier.
  ctx.strokeStyle = "rgba(90,115,150,.1)";
  ctx.lineWidth = 1.4;
  for (const side of [-0.035, 0.035]) {
    ctx.beginPath();
    let started = false;
    for (const t of run.trail) {
      const k = projK(t.z);
      const x = sxOf(t.u + side, k);
      const y = standY(t.z, k, x);
      if (y < horizon) continue;
      if (started) ctx.lineTo(x, y); else { ctx.moveTo(x, y); started = true; }
    }
    if (started) ctx.stroke();
  }

  // Painter's algorithm: furthest first, so nearer things overlap them.
  const sorted = [...run.objs].sort((a, b) => b.z - a.z);
  for (const o of sorted) {
    // Stop drawing the moment something is level with the skier. Past that the
    // perspective divide doubles its size and a single fir fills the screen and
    // covers the goat. Collision still resolves in a window either side of zero;
    // this only governs what is painted.
    if (o.z < -0.02) continue;
    const k = projK(o.z);
    const x = sxOf(o.u, k);
    if (x < -140 || x > W + 140) continue;
    const y = standY(o.z, k, x);
    if (y < horizon - 6 || y > H + 90) continue;
    drawObject(ctx, o, x, y, k, pal, sunX);
  }

  for (const p of run.puffs) {
    ctx.fillStyle = `rgba(255,255,255,${Math.min(0.8, p.life * 2)})`;
    ctx.beginPath(); ctx.arc(p.x, p.y, 2 + p.life * 7, 0, Math.PI * 2); ctx.fill();
  }

  if (mountain.wind > 0.25) {
    ctx.fillStyle = "rgba(255,255,255,.45)";
    for (let i = 0; i < 34; i += 1) {
      const fx = (i * 113 - run.u * 90 + run.dist * 40) % W;
      ctx.fillRect(fx < 0 ? fx + W : fx, (i * 71 + run.dist * 130) % H, 2, 4);
    }
  }

  drawGoat(ctx, W / 2, standY(0, 1, W / 2), run, pal);

  // The chaser, rising into frame from behind as it closes.
  if (run.chase !== null && !run.caught && run.chase < 1.05) {
    drawChaser(ctx, W / 2 + run.lean * 26, syOf(1) + 60 - (1 - run.chase / 1.05) * 150, pal, run.dist);
  }

  // A soft vignette. The cheapest thing that stops a canvas looking like a
  // canvas and starts it looking like a photograph of somewhere.
  const vig = ctx.createRadialGradient(W / 2, H * 0.52, H * 0.32, W / 2, H * 0.52, H * 0.82);
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(1, "rgba(15,23,42,.24)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);
}

// A ridgeline of overlapping triangles, drawn as one silhouette.
function drawRange(ctx: CanvasRenderingContext2D, shift: number, baseY: number, height: number, spread: number, jag: number) {
  ctx.beginPath();
  ctx.moveTo(-40, baseY);
  for (let x = -40; x <= W + 40; x += 6) {
    const n = (x + shift) / spread;
    const h = Math.abs(Math.sin(n)) * height + Math.abs(Math.sin(n * jag * 2.3)) * height * 0.34;
    ctx.lineTo(x, baseY - h);
  }
  ctx.lineTo(W + 40, baseY);
  ctx.closePath();
  ctx.fill();
}

function tracePeak(
  ctx: CanvasRenderingContext2D, profile: [number, number][],
  left: number, right: number, baseY: number, height: number,
) {
  ctx.beginPath();
  ctx.moveTo(left, baseY);
  for (const [px, py] of profile) ctx.lineTo(left + (right - left) * px, baseY - py * height);
  ctx.lineTo(right, baseY);
  ctx.closePath();
}

function drawObject(
  ctx: CanvasRenderingContext2D, o: Obj, x: number, y: number, k: number, pal: Palette, sunX: number,
) {
  // Aerial perspective: far objects fade toward the horizon colour rather than
  // just shrinking. Nothing else does as much for the sense of distance.
  const fade = Math.max(0, Math.min(0.72, (1 - k) * 0.9));
  const ink = mix(pal.ink, pal.sky[2], fade);
  const s = k;

  // Long shadows, all leaning away from the sun. Alto's whole ground plane is
  // held together by these.
  const shadow = (len: number, wide: number) => {
    const dir = x < sunX ? -1 : 1;
    ctx.fillStyle = `rgba(40,58,88,${0.16 * k})`;
    ctx.beginPath();
    ctx.ellipse(x + dir * len * 0.5 * s, y + 2 * s, len * s, wide * s, dir * 0.42, 0, Math.PI * 2);
    ctx.fill();
  };

  if (o.kind === "coin") {
    ctx.fillStyle = mix("#F6C453", pal.sky[2], fade * 0.5);
    ctx.beginPath(); ctx.arc(x, y - 52 * s, 11 * s, 0, Math.PI * 2); ctx.fill();
    return;
  }

  if (o.kind === "ramp") {
    shadow(34, 9);
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.moveTo(x - 40 * s, y + 4 * s); ctx.lineTo(x + 40 * s, y + 4 * s);
    ctx.lineTo(x + 30 * s, y - 26 * s); ctx.lineTo(x - 30 * s, y - 26 * s);
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = mix("#6FD3F7", pal.sky[2], fade);
    ctx.fillRect(x - 30 * s, y - 32 * s, 60 * s, 7 * s);
    return;
  }

  if (o.kind === "gate") {
    for (const side of [-1, 1]) {
      const fx = x + side * 52 * s;
      ctx.fillStyle = ink;
      ctx.fillRect(fx - 2 * s, y - 44 * s, 4 * s, 46 * s);
      ctx.fillStyle = mix(side < 0 ? "#E4574B" : "#4C8DF6", pal.sky[2], fade);
      ctx.beginPath();
      ctx.moveTo(fx, y - 44 * s);
      ctx.lineTo(fx - side * 22 * s, y - 36 * s);
      ctx.lineTo(fx, y - 28 * s);
      ctx.closePath(); ctx.fill();
    }
    return;
  }

  if (o.kind === "cabin") {
    shadow(46, 12);
    ctx.fillStyle = ink;
    ctx.fillRect(x - 30 * s, y - 34 * s, 60 * s, 36 * s);
    ctx.beginPath();
    ctx.moveTo(x - 38 * s, y - 34 * s); ctx.lineTo(x, y - 62 * s); ctx.lineTo(x + 38 * s, y - 34 * s);
    ctx.closePath(); ctx.fill();
    // One lit window, which does more for the mood than anything else here.
    ctx.fillStyle = mix("#F6C453", pal.sky[2], fade * 0.4);
    ctx.fillRect(x - 8 * s, y - 26 * s, 17 * s, 14 * s);
    return;
  }

  if (o.kind === "rock") {
    shadow(26, 8);
    ctx.fillStyle = ink;
    ctx.beginPath(); ctx.ellipse(x, y - 9 * s, 18 * s, 13 * s, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x - 11 * s, y - 3 * s, 10 * s, 7 * s, 0, 0, Math.PI * 2); ctx.fill();
    return;
  }

  // Trees, and the boundary treeline, which is the same shape further out.
  const tall = o.kind === "flagline" ? 74 : 88;
  shadow(o.kind === "flagline" ? 30 : 38, 9);
  ctx.fillStyle = ink;
  ctx.fillRect(x - 3 * s, y - tall * 0.2 * s, 6 * s, tall * 0.2 * s);
  for (let tier = 0; tier < 3; tier += 1) {
    const top = y - tall * s + tier * tall * 0.26 * s;
    const wide = (13 + tier * 7) * s;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x + wide, top + tall * 0.36 * s);
    ctx.lineTo(x - wide, top + tall * 0.36 * s);
    ctx.closePath(); ctx.fill();
  }
  // Snow catching the light on the topmost branches.
  ctx.fillStyle = `rgba(255,255,255,${0.6 - fade * 0.5})`;
  ctx.beginPath();
  ctx.moveTo(x, y - tall * s);
  ctx.lineTo(x + 7 * s, y - tall * 0.83 * s);
  ctx.lineTo(x - 7 * s, y - tall * 0.83 * s);
  ctx.closePath(); ctx.fill();
}

// Something large and shaggy, seen from behind as it closes on you. Kept
// friendly: it is a bit silly, and it only ever costs you the rest of a run you
// have already been paid for.
function drawChaser(ctx: CanvasRenderingContext2D, x: number, y: number, pal: Palette, dist: number) {
  const bob = Math.sin(dist * 6) * 4;
  ctx.fillStyle = pal.ink;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(x + side * 42, y + bob - Math.sin(dist * 6 + side) * 10, 13, 30, side * 0.28, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath(); ctx.ellipse(x, y + bob, 40, 50, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x, y - 46 + bob, 25, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath(); ctx.arc(x - 9, y - 50 + bob, 5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + 9, y - 50 + bob, 5, 0, Math.PI * 2); ctx.fill();
  ctx.fillRect(x - 14, y - 36 + bob, 28, 8);
}

// The mascot: a goat on skis, from behind, in silhouette. A goat on skis is
// funnier than a person on skis, and in flat ink it needs no outline at all,
// which is also why it stays legible against snow.
function drawGoat(ctx: CanvasRenderingContext2D, x: number, y: number, run: Run, pal: Palette) {
  const airborne = run.air > 0;
  const lift = airborne ? Math.sin((1 - run.air / AIR_SECONDS) * Math.PI) * 44 : 0;

  // The shadow stays on the snow and shrinks, which is what sells the air.
  ctx.fillStyle = `rgba(40,58,88,${0.2 - lift * 0.003})`;
  ctx.beginPath();
  ctx.ellipse(x, y + 20, 22 - lift * 0.18, 6 - lift * 0.055, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.translate(x, y - lift);
  ctx.rotate(run.tumble > 0 ? (TUMBLE_SECONDS - run.tumble) * 9 : run.lean * 0.3);

  // Scaled down and narrowed from the first pass, which read as a beetle: a
  // round body with two horns on top and legs out the sides is an insect from
  // behind, whatever you meant it to be. A goat is taller than it is wide, its
  // horns sweep back rather than up, and it has a visible rump.
  ctx.scale(0.86, 0.86);

  // Poles: thin, short, and angled steeply down and back. The first pass had
  // them long and near-horizontal, which together with a round body read as an
  // insect's legs. Everything about this shape is chosen to avoid that.
  ctx.strokeStyle = pal.ink; ctx.lineCap = "round"; ctx.lineWidth = 2.2;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(side * 9, -6);
    ctx.lineTo(side * 15 + run.lean * 5, 24);
    ctx.stroke();
  }

  ctx.fillStyle = "#DC2626";                                     // skis, seen end-on
  ctx.fillRect(-12, 12, 6, 28);
  ctx.fillRect(6, 12, 6, 28);

  ctx.fillStyle = pal.ink;
  // One continuous body, widest at the rump and tapering to the shoulders. A
  // single tapered shape reads as an animal; a stack of circles reads as a bug.
  ctx.beginPath();
  ctx.moveTo(-11, 16);
  ctx.quadraticCurveTo(-14, -2, -8, -16);
  ctx.quadraticCurveTo(0, -22, 8, -16);
  ctx.quadraticCurveTo(14, -2, 11, 16);
  ctx.quadraticCurveTo(0, 21, -11, 16);
  ctx.closePath(); ctx.fill();

  // Neck and head, small and sitting forward, so there is a silhouette above
  // the shoulders rather than a lump on top of a circle.
  ctx.beginPath(); ctx.ellipse(0, -21, 5.5, 7, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(0, -30, 6.5, 5.5, 0, 0, Math.PI * 2); ctx.fill();

  // Horns curving back and down along the neck, close to the head. Short and
  // low is what says goat; long and upright says antennae.
  ctx.strokeStyle = pal.ink; ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(-4, -34); ctx.quadraticCurveTo(-11, -34, -9, -25);
  ctx.moveTo(4, -34); ctx.quadraticCurveTo(11, -34, 9, -25);
  ctx.stroke();

  // A short flick of a tail, breaking the outline.
  ctx.fillStyle = pal.ink;
  ctx.beginPath();
  ctx.moveTo(-1, -6); ctx.lineTo(-7, -17); ctx.lineTo(3, -8);
  ctx.closePath(); ctx.fill();

  // The one spot of colour on the whole goat, which is what your eye tracks.
  // A band across the shoulders with the tail of the scarf streaming behind.
  ctx.fillStyle = "#F5A524";
  ctx.beginPath();
  ctx.moveTo(-8, -17); ctx.lineTo(8, -17); ctx.lineTo(7, -12); ctx.lineTo(-7, -12);
  ctx.closePath(); ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-2, -14); ctx.lineTo(-7 - run.lean * 28, 8); ctx.lineTo(3, -13);
  ctx.closePath(); ctx.fill();

  ctx.restore();
}
